import { CaretDownIcon, CaretUpIcon, CheckIcon } from '@phosphor-icons/react';
import * as SelectPrimitive from '@radix-ui/react-select';
import { type VariantProps, cva } from 'class-variance-authority';
import * as React from 'react';

import { useDialogContentContainer } from '@/components/ui/dialog';
import { OVERLAY_POP_MOTION, SECTION_LABEL } from '@/lib/ui-tokens';
import { cn } from '@/lib/utils';

const Select = SelectPrimitive.Root;

const SelectGroup = SelectPrimitive.Group;

const SelectValue = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Value>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Value>
>(({ className, ...props }, ref) => (
  <SelectPrimitive.Value
    ref={ref}
    // Use the same placeholder color as input::placeholder
    className={cn('data-[placeholder]:text-muted-foreground text-foreground', className)}
    {...props}
  />
));
SelectValue.displayName = 'SelectValue';

/**
 * Trigger size variants — the mirror of `inputVariants` in `input.tsx`.
 *
 * Select was the only form primitive with its height welded into the base
 * string (`h-10`), so a dense dialog could not put a Select next to a
 * `size="sm"` Input without the two disagreeing by 4px. Both variants now
 * match `inputVariants` exactly: 44px touch target on mobile, 32px (default)
 * or 28px (`sm`) on desktop.
 *
 * `default` was a bare `h-10`, which is 40px on mobile too — BELOW the 44px
 * touch-target floor. The `h-11 md:` form fixes that as a side effect.
 * Never pass a bare `h-8`/`h-9` at a call site: tailwind-merge only dedupes
 * within matching modifier groups, so it strips the `h-11` and leaves the
 * `md:` half standing.
 */
const selectTriggerVariants = cva(
  cn(
    // `dark:bg-input`, matching `inputVariants`/`textareaVariants`. Radix
    // renders this as a `<button role="combobox">`, never a native
    // `<select>` — the dark-mode-only global forcing rule in index.css
    // (`[data-theme='dark'] select`) can ONLY match a real `<select>` tag, so
    // it structurally cannot reach this trigger. That gap is exactly why the
    // Input and SelectTrigger in the same form row used to render two
    // different dark-mode fills (Input forced to `--input`, SelectTrigger
    // left on `--background`, invisible against the page) — measured live on
    // `/settings/company`, 2026-08-26. Declaring `dark:bg-input` here
    // directly, instead of depending on that global rule, fixes it at the
    // source and stops relying on a selector that can't see this control.
    // No `shadow-sm`/`shadow-ring` on focus — see the matching note in
    // `inputVariants`: it is a permanently dead layer app-wide.
    'flex w-full items-center justify-between rounded-[var(--radius)] border border-border bg-background dark:bg-input px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:border-ring/60 focus-visible:ring-1 focus-visible:ring-ring/30 disabled:cursor-not-allowed disabled:opacity-50 transition duration-200 ease-out [&>span]:line-clamp-1',
    // Add placeholder-muted-foreground for placeholder text
    '[&_[data-placeholder]]:text-muted-foreground',
  ),
  {
    variants: {
      size: {
        default: 'h-11 md:h-8 px-2.5 py-1',
        sm: 'h-11 md:h-7 px-2 py-0.5',
      },
    },
    defaultVariants: {
      size: 'default',
    },
  },
);

const SelectTrigger = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Trigger>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Trigger> &
    VariantProps<typeof selectTriggerVariants>
>(({ className, children, size, ...props }, ref) => (
  <SelectPrimitive.Trigger
    ref={ref}
    // Use border-border, bg-card like Input. Ensure rounded-md uses theme radius.
    className={cn(selectTriggerVariants({ size }), className)}
    {...props}
  >
    {children}
    <SelectPrimitive.Icon asChild>
      <CaretDownIcon className="h-4 w-4 opacity-50" />
    </SelectPrimitive.Icon>
  </SelectPrimitive.Trigger>
));
SelectTrigger.displayName = 'SelectTrigger';

const SelectScrollUpButton = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.ScrollUpButton>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.ScrollUpButton>
>(({ className, ...props }, ref) => (
  <SelectPrimitive.ScrollUpButton
    ref={ref}
    className={cn('flex cursor-default items-center justify-center py-1', className)}
    {...props}
  >
    <CaretUpIcon className="h-4 w-4" />
  </SelectPrimitive.ScrollUpButton>
));
SelectScrollUpButton.displayName = 'SelectScrollUpButton';

// Fix export and definition order for SelectScrollDownButton
const SelectScrollDownButton = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.ScrollDownButton>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.ScrollDownButton>
>(({ className, ...props }, ref) => (
  <SelectPrimitive.ScrollDownButton
    ref={ref}
    className={cn('flex cursor-default items-center justify-center py-1', className)}
    {...props}
  >
    <CaretDownIcon className="h-4 w-4" />
  </SelectPrimitive.ScrollDownButton>
));
SelectScrollDownButton.displayName = 'SelectScrollDownButton';

const SelectContent = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Content> & {
    container?: HTMLElement | null;
  }
>(({ className, children, position = 'popper', container, ...props }, ref) => {
  const dialogContainer = useDialogContentContainer();
  return (
    <SelectPrimitive.Portal container={container ?? dialogContainer}>
      <SelectPrimitive.Content
        ref={ref}
        className={cn(
          'relative z-50 max-h-[min(50vh,400px)] overflow-hidden rounded-xl border border-border/60 bg-popover/95 backdrop-blur-xl shadow-elevation-3 text-popover-foreground origin-(--radix-select-content-transform-origin)',
          OVERLAY_POP_MOTION,
          position === 'popper' &&
            'data-[side=bottom]:translate-y-1 data-[side=left]:-translate-x-1 data-[side=right]:translate-x-1 data-[side=top]:-translate-y-1',
          className,
        )}
        position={position}
        {...props}
      >
        <SelectScrollUpButton />
        <SelectPrimitive.Viewport
          className={cn(
            'p-1',
            position === 'popper' &&
              'h-[var(--radix-select-trigger-height)] w-full min-w-[var(--radix-select-trigger-width)]',
          )}
        >
          {children}
        </SelectPrimitive.Viewport>
        <SelectScrollDownButton />
      </SelectPrimitive.Content>
    </SelectPrimitive.Portal>
  );
});
SelectContent.displayName = 'SelectContent';

const SelectLabel = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Label>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Label>
>(({ className, ...props }, ref) => (
  <SelectPrimitive.Label
    ref={ref}
    // `SECTION_LABEL`, not a bespoke 14px semibold. It was the last heading
    // in the app that opened inside a menu at a different size and case
    // from the 127 files using the shared eyebrow token.
    className={cn('py-1.5 pl-2 pr-8', SECTION_LABEL, className)}
    {...props}
  />
));
SelectLabel.displayName = 'SelectLabel';

const SelectItem = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Item>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Item> & {
    hideIndicator?: boolean;
  }
>(({ className, children, hideIndicator, ...props }, ref) => (
  <SelectPrimitive.Item
    ref={ref}
    className={cn(
      'relative flex w-full cursor-default select-none items-center rounded-sm py-1.5 text-sm outline-none transition-colors',
      hideIndicator ? 'px-2' : 'pl-2 pr-8',
      // ONE signal for one fact. The selected row used to carry a check AND a
      // filled background — `data-[state=checked]:bg-muted` — so every
      // `<SelectItem>` in the app (44 files) stated its selection twice, and the
      // fill then collided with the hover fill on the same rows. The trailing
      // check is the signal (TOOLBAR-DOCTRINE §12.3); the background belongs to
      // hover/focus alone, so moving the pointer over an unselected row no
      // longer makes it look selected.
      'focus:bg-muted focus:text-foreground data-[state=checked]:text-foreground',
      className,
    )}
    {...props}
  >
    <SelectPrimitive.ItemText>{children}</SelectPrimitive.ItemText>
    {!hideIndicator && (
      <span className="absolute right-2 flex h-3.5 w-3.5 items-center justify-center">
        <SelectPrimitive.ItemIndicator>
          <CheckIcon className="size-3.5" weight="bold" />
        </SelectPrimitive.ItemIndicator>
      </span>
    )}
  </SelectPrimitive.Item>
));
SelectItem.displayName = 'SelectItem';

const SelectSeparator = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Separator>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Separator>
>(({ className, ...props }, ref) => (
  <SelectPrimitive.Separator
    ref={ref}
    className={cn('-mx-1 my-1 h-px bg-muted', className)}
    {...props}
  />
));
SelectSeparator.displayName = 'SelectSeparator';

export {
  Select,
  SelectGroup,
  SelectValue,
  SelectTrigger,
  SelectContent,
  SelectLabel,
  SelectItem,
  SelectSeparator,
  SelectScrollUpButton,
  SelectScrollDownButton,
  selectTriggerVariants,
};
